/**
 * Stage 3.5 — Card view.
 *
 * Side-by-side: PDF on the left, fields on the right. Arrow nav between
 * extracted files. All fields are editable. Coding can be set here too.
 * Raw response is viewable / copy-able.
 */

import { getState, setState, updateFile } from '../state.js';
import { distributionTotalDelta } from '../lib/rules.js';
import { el, clear, toast } from './dom.js';
import { renderPageToCanvas, getPageCount } from '../pdf.js';
import { saveBatchSnapshot } from '../persistence.js';
import { openRulesEditor } from './rules-editor.js';

let mounted = null;
let keyHandler = null;
const selectedCardLineByFileId = new Map();

export function renderCardStage(mount) {
  clear(mount);
  mounted = mount;

  const state = getState();
  const reviewable = state.files.filter(f => f.status === 'done' || f.status === 'failed');
  if (reviewable.length === 0) {
    mount.appendChild(el('div', { class: 'panel panel-empty' }, [
      el('h2', {}, ['Nothing to review']),
      el('div', { class: 'actions' }, [
        el('button', { class: 'primary-btn', onClick: () => setState({ stage: 'review' }) }, ['Back'])
      ])
    ]));
    return;
  }

  let idx = state.cardIndex;
  if (idx < 0 || idx >= state.files.length) {
    idx = state.files.findIndex(f => f.status === 'done' || f.status === 'failed');
  }
  const f = state.files[idx];

  const wrap = el('div');
  wrap.appendChild(el('div', { class: 'path-bar' }, [
    el('div', {}, [
      el('span', { class: 'label' }, ['Reviewing']),
      el('span', { class: 'path' }, [f.name])
    ]),
    el('div', { class: 'actions' }, [
      el('button', { class: 'ghost-btn', onClick: () => setState({ stage: 'review' }) }, ['← Back to table'])
    ])
  ]));

  const view = el('div', { class: 'card-view' });
  view.appendChild(renderPdfPane(f));
  view.appendChild(renderFieldsPane(f, idx, state.files.length));
  wrap.appendChild(view);

  mount.appendChild(wrap);

  // Keyboard nav
  if (keyHandler) document.removeEventListener('keydown', keyHandler);
  keyHandler = (ev) => {
    const tag = ev.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (ev.key === 'ArrowLeft')  goPrev(idx);
    else if (ev.key === 'ArrowRight') goNext(idx);
    else if (ev.key === 'Escape') setState({ stage: 'review' });
  };
  document.addEventListener('keydown', keyHandler);
}

export function teardownCardStage() {
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }
}

function goPrev(idx) {
  const files = getState().files;
  for (let i = idx - 1; i >= 0; i--) {
    if (files[i].status === 'done' || files[i].status === 'failed') {
      setState({ cardIndex: i });
      return;
    }
  }
}

function goNext(idx) {
  const files = getState().files;
  for (let i = idx + 1; i < files.length; i++) {
    if (files[i].status === 'done' || files[i].status === 'failed') {
      setState({ cardIndex: i });
      return;
    }
  }
}

function renderPdfPane(f) {
  const pane = el('div', { class: 'pdf-pane' });
  const pageInfo = el('span', { id: 'pdf-page-info' }, ['Loading…']);
  pane.appendChild(el('div', { class: 'pdf-toolbar' }, [
    pageInfo,
    el('span', {}, [`${(f.size / 1024).toFixed(0)} KB`])
  ]));

  const canvasWrap = el('div', { class: 'pdf-canvas-wrap' });
  pane.appendChild(canvasWrap);

  (async () => {
    try {
      const pageCount = await getPageCount(f.file);
      pageInfo.textContent = `${pageCount} page${pageCount === 1 ? '' : 's'}`;
      const targetWidth = Math.min(720, canvasWrap.clientWidth - 36) || 600;
      for (let p = 1; p <= pageCount; p++) {
        const canvas = await renderPageToCanvas(f.file, p, targetWidth);
        canvasWrap.appendChild(canvas);
      }
    } catch (err) {
      canvasWrap.appendChild(el('div', {
        style: { padding: '20px', color: 'var(--danger-fg)' }
      }, [`Could not render PDF: ${err.message}`]));
    }
  })();

  return pane;
}

function renderFieldsPane(f, idx, total) {
  const pane = el('div', { class: 'fields-pane' });

  pane.appendChild(el('div', { class: 'fields-toolbar' }, [
    el('span', {}, ['Extracted fields']),
    el('span', {}, [`${idx + 1} of ${total}`])
  ]));

  const body = el('div', { class: 'fields-body' });

  if (f.status === 'failed') {
    body.appendChild(el('div', { class: 'error-detail' }, [f.error || 'Extraction failed.']));
    pane.appendChild(body);
    pane.appendChild(navFooter(idx));
    return pane;
  }

  body.appendChild(editField(f, 'vendor_name',    'Vendor',       false));
  body.appendChild(row2(
    editField(f, 'invoice_number', 'Invoice #', false),
    editField(f, 'currency',       'Currency',  false)
  ));
  body.appendChild(row2(
    editField(f, 'invoice_date',   'Invoice date', false),
    editField(f, 'due_date',       'Due date',     false)
  ));
  body.appendChild(row2(
    editField(f, 'total_amount',   'Total amount', true),
    editField(f, 'tax_amount',     'Tax amount',   true)
  ));

  const dDelta = distributionTotalDelta(f);
  if (dDelta != null) {
    body.appendChild(el('div', {
      class: 'panel',
      style: {
        fontSize: '12px',
        background: 'var(--warn-bg)',
        borderColor: 'var(--warn)',
        color: 'var(--warn-fg)',
        marginBottom: '8px'
      }
    }, [`Detail lines total differs from invoice total by ${dDelta.toFixed(2)} (tolerance 0.02). Check tax lines or rounding.`]));
  }

  // Coding section
  body.appendChild(el('div', { class: 'field-group' }, [
    el('label', {}, ['Coding']),
    renderCodingEditors(f)
  ]));

  if (Array.isArray(f.extracted?.line_items) && f.extracted.line_items.length > 0) {
    const selectedLineIndex = selectedCardLineByFileId.get(f.id);
    const selectedLine = getSelectedCardLine(f, selectedLineIndex);
    body.appendChild(el('div', { class: 'field-group' }, [
      el('label', {}, [`Line items (${f.extracted.line_items.length})`]),
      renderLineItems(f.extracted.line_items, selectedLineIndex, (lineIndex) => {
        selectedCardLineByFileId.set(f.id, lineIndex);
        setState({});
      }),
      el('div', { class: 'card-line-rule-actions' }, [
        el('div', { class: 'card-line-rule-hint' }, [
          selectedLine
            ? 'Line selected. Add a keyword rule from this description.'
            : 'Select a line item, then add a keyword rule.'
        ]),
        el('button', {
          class: 'ghost-btn',
          style: { fontSize: '11px' },
          disabled: !selectedLine,
          onClick: () => addKeywordRuleFromSelectedLine(f, selectedLine)
        }, ['Add rule from selected line…'])
      ])
    ]));
  }

  const taxes = f.extracted?.sales_tax_lines;
  if (Array.isArray(taxes) && taxes.length > 0) {
    body.appendChild(el('div', { class: 'field-group' }, [
      el('label', {}, ['Sales tax lines']),
      el('div', { style: { fontSize: '13px' } }, taxes.map(t => el('div', { style: { marginBottom: '4px' } }, [
        `${t.label || 'Tax'}: `,
        typeof t.amount === 'number' ? fmtNum(t.amount) : '—'
      ])))
    ]));
  }

  if (f.extracted?.raw_text) {
    body.appendChild(el('details', { style: { fontSize: '12px', color: 'var(--text-secondary)' } }, [
      el('summary', { style: { cursor: 'pointer', padding: '4px 0' } }, ['Raw transcription']),
      el('pre', {
        style: {
          fontFamily: 'var(--font-mono)', fontSize: '11px', whiteSpace: 'pre-wrap',
          background: 'var(--bg-secondary)', padding: '10px', borderRadius: '4px',
          maxHeight: '220px', overflow: 'auto', margin: '4px 0 0'
        }
      }, [f.extracted.raw_text])
    ]));
  }

  // Raw API response — copy button
  if (f.extracted) {
    body.appendChild(el('div', { style: { marginTop: '8px' } }, [
      el('button', {
        class: 'ghost-btn',
        style: { fontSize: '11px' },
        onClick: () => {
          const text = JSON.stringify(f.extracted, null, 2);
          navigator.clipboard.writeText(text).then(
            () => toast('Copied extracted JSON to clipboard.', 'success'),
            () => toast('Clipboard write failed.', 'error')
          );
        }
      }, ['Copy extracted JSON'])
    ]));
  }

  pane.appendChild(body);
  pane.appendChild(navFooter(idx));
  return pane;
}

/* -------- Editable field with edit toggle -------- */

function editField(f, field, label, isAmount) {
  const v = getEffectiveValue(f, field);
  const wrap = el('div', { class: 'field-group' });
  wrap.appendChild(el('label', {}, [label]));

  const isMissing = v == null || v === '';
  const display = isMissing
    ? el('div', { class: 'val missing', tabindex: 0 }, ['— not present —'])
    : el('div', { class: 'val', tabindex: 0 }, [formatForDisplay(v, isAmount)]);

  display.title = 'Click to edit';
  display.style.cursor = 'text';

  display.addEventListener('click', () => {
    const input = el('input', {
      type: 'text',
      class: 'field-input',
      value: isMissing ? '' : String(v)
    });
    wrap.replaceChild(input, display);
    input.focus();
    input.select();

    const commit = () => {
      let raw = input.value.trim();
      let newValue = raw === '' ? null : raw;
      if (isAmount && newValue != null) {
        const n = Number(newValue);
        if (!Number.isFinite(n)) {
          toast('Amount must be a number.', 'error');
          input.focus();
          return;
        }
        newValue = n;
      }
      setEdit(f, field, newValue);
    };
    const cancel = () => setState({});  // re-render

    input.addEventListener('blur', () => { commit(); setState({}); });
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); commit(); setState({}); }
      else if (ev.key === 'Escape') { ev.preventDefault(); cancel(); }
    });
  });

  wrap.appendChild(display);
  return wrap;
}

function renderCodingEditors(f) {
  const c = f.coding || { glCode: '', propertyCode: '', source: null };
  const gl   = el('input', { type: 'text', class: 'field-input', value: c.glCode || '', placeholder: 'GL code' });
  const prop = el('input', { type: 'text', class: 'field-input', value: c.propertyCode || '', placeholder: 'Property code' });

  const commit = () => {
    const next = {
      glCode:       gl.value.trim(),
      propertyCode: prop.value.trim(),
      source:       'manual'
    };
    updateFile(f.id, { coding: next });
    saveBatchSnapshot().catch(() => {});
  };
  gl.addEventListener('blur', commit);
  prop.addEventListener('blur', commit);

  return el('div', { class: 'field-row' }, [
    el('div', {}, [
      el('label', { class: 'field-label' }, ['GL']),
      gl
    ]),
    el('div', {}, [
      el('label', { class: 'field-label' }, ['Property']),
      prop
    ])
  ]);
}

function getEffectiveValue(f, field) {
  if (f.edits && f.edits[field] != null) return f.edits[field];
  return f.extracted ? f.extracted[field] : null;
}

function setEdit(f, field, value) {
  const edits = { ...(f.edits || {}), [field]: value };
  if (f.extracted && f.extracted[field] === value) delete edits[field];
  updateFile(f.id, { edits });
  saveBatchSnapshot().catch(() => {});
}

function navFooter(idx) {
  const files = getState().files;
  const hasPrev = files.slice(0, idx).some(x => x.status === 'done' || x.status === 'failed');
  const hasNext = files.slice(idx + 1).some(x => x.status === 'done' || x.status === 'failed');

  return el('div', { class: 'card-nav' }, [
    el('button', { class: 'ghost-btn', disabled: !hasPrev, onClick: () => goPrev(idx) }, ['← Previous']),
    el('span', {}, ['← / → to navigate, Esc to close']),
    el('button', { class: 'ghost-btn', disabled: !hasNext, onClick: () => goNext(idx) }, ['Next →'])
  ]);
}

function row2(a, b) { return el('div', { class: 'field-row' }, [a, b]); }

function formatForDisplay(v, isAmount) {
  if (v == null || v === '') return '';
  if (isAmount && typeof v === 'number') {
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(v);
}

function renderLineItems(items, selectedLineIndex = null, onSelect = null) {
  const wrap = el('div', { class: 'line-items' });
  wrap.appendChild(el('div', { class: 'li-row li-head' }, [
    el('span', {}, ['Description']),
    el('span', { class: 'li-amt' }, ['Item code']),
    el('span', { class: 'li-amt' }, ['Qty']),
    el('span', { class: 'li-amt' }, ['Unit']),
    el('span', { class: 'li-amt' }, ['Total'])
  ]));
  for (let i = 0; i < items.length; i++) {
    const li = items[i];
    const isSelected = i === selectedLineIndex;
    wrap.appendChild(el('div', {
      class: `li-row li-item${isSelected ? ' selected' : ''}`,
      onClick: () => { if (onSelect) onSelect(i); }
    }, [
      el('span', { title: li.description || '' }, [
        li.description || '—',
        isSelected ? el('span', { class: 'card-selected-line-badge' }, ['Selected for rule']) : null
      ]),
      el('span', { class: 'li-amt', title: li.item_code || '' }, [li.item_code || '—']),
      el('span', { class: 'li-amt' }, [li.quantity != null ? String(li.quantity) : '—']),
      el('span', { class: 'li-amt' }, [li.unit_price != null ? fmtNum(li.unit_price) : '—']),
      el('span', { class: 'li-amt' }, [li.line_total != null ? fmtNum(li.line_total) : '—'])
    ]));
  }
  return wrap;
}

function getSelectedCardLine(f, lineIndex) {
  const e = { ...f.extracted, ...(f.edits || {}) };
  const items = Array.isArray(e.line_items) ? e.line_items : [];
  const idx = Number(lineIndex);
  if (!Number.isInteger(idx) || idx < 0 || idx >= items.length) return null;
  return items[idx];
}

function addKeywordRuleFromSelectedLine(f, selectedLine) {
  if (!selectedLine) {
    toast('Select a line item first.', 'error');
    return;
  }
  const description = String(selectedLine.description || '').trim();
  if (!description) {
    toast('Selected line has no description for keyword matching.', 'error');
    return;
  }

  const vendorMatch = String(getEffectiveValue(f, 'vendor_name') || '').trim();
  const coding = f.coding || {};
  const glCodeDefault = String(coding.glCode || '').trim();
  const propertyCodeDefault = String(coding.propertyCode || '').trim();
  if (!vendorMatch) {
    toast('Vendor is required to create a rule.', 'error');
    return;
  }
  if (!glCodeDefault && !propertyCodeDefault) {
    toast('Set GL or property coding before creating a rule.', 'error');
    return;
  }

  const draft = {
    vendorMatch,
    glCodeDefault,
    propertyCodeDefault,
    taxGl: glCodeDefault,
    emitDistributionLines: true,
    lineItemRules: [{
      matchOn: 'keyword',
      matchValue: description,
      glCode: glCodeDefault,
      propertyCode: propertyCodeDefault || null
    }],
    note: ''
  };

  openRulesEditor(
    () => setState({}),
    draft,
    { createThenEditFromDraft: true }
  );
}

function fmtNum(n) {
  if (typeof n !== 'number') return String(n);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
